import { motion } from "framer-motion";
import { Award, Users, Home, TrendingUp } from "lucide-react";

// Import WebP versions at different sizes
import agentHeadshotSmall from "@/assets/brant-booker.jpg?w=400&format=webp&quality=80";
import agentHeadshotMedium from "@/assets/brant-booker.jpg?w=600&format=webp&quality=80";
import agentHeadshotLarge from "@/assets/brant-booker.jpg?w=800&format=webp&quality=80";
import agentHeadshotFallback from "@/assets/brant-booker.jpg";

const achievements = [
  {
    icon: Award,
    title: "Top Producer",
    description: "Consistently recognized as one of Chicago's top real estate producers",
  },
  {
    icon: TrendingUp,
    title: "$900M+ Sales",
    description: "Over $900 million in real estate sold and developed",
  },
  {
    icon: Users,
    title: "1,900+ Transactions",
    description: "Trusted by nearly 2,000 clients to handle their most important investments",
  },
  {
    icon: Home,
    title: "Full-Service Expert",
    description: "Luxury, new construction, resale, leasing, and investment properties",
  },
];

const About = () => {
  return (
    <section id="about" className="py-24 bg-secondary">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image with WebP and responsive srcset */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-[4/5] bg-muted rounded-sm overflow-hidden">
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${agentHeadshotSmall} 400w, ${agentHeadshotMedium} 600w, ${agentHeadshotLarge} 800w`}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <img 
                  src={agentHeadshotFallback} 
                  alt="Brant Booker - Chicago Real Estate Agent with Jameson Sotheby's International Realty"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={1000}
                />
              </picture>
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-accent rounded-sm flex items-center justify-center">
              <div className="text-center text-accent-foreground">
                <div className="text-4xl font-display font-bold">20+</div>
                <div className="text-sm font-medium">Years of Excellence</div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="text-accent text-sm tracking-[0.2em] uppercase font-medium">
              About Brant
            </span>
            <h2 className="text-4xl md:text-5xl font-display font-semibold text-foreground mt-4 mb-6">
              Your Trusted Partner in Chicago Real Estate
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              Born and raised in a family of realtors, Brant Booker has spent the past 20 years 
              building long-term relationships based on trust with clients and colleagues alike. 
              He is well versed in all aspects of real estate including luxury, new construction, 
              resale, buyer brokerage, leasing, and investments.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-10">
              Brant loves selling real estate because he loves helping his clients through the 
              acquisition and disposition of their most important investments. His expertise in 
              procuring land for development projects has led to successful ground-up developments 
              of mid-size apartment buildings and single-family luxury homes throughout Chicago.
            </p>

            {/* Achievements Grid */}
            <div className="grid grid-cols-2 gap-6">
              {achievements.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-4"
                >
                  <div className="w-12 h-12 rounded-sm bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
